import _ from 'lodash';
import api from '@js/api';
import ErrorHandler from '@js/ErrorHandler';

export const DEFAULT_ADDRESS = {
	id: undefined,
	address_id: undefined,
	company_name: '',
	first_name: '',
	last_name: '',
	address: '',
	address2: '',
	city: '',
	postal_code: '',
	country: 'USA',
	state: '',
	phone_number: '',
};

export const ADDRESS_REQUIRED_FIELDS = [
	'first_name',
	'last_name',
	'country',
	'address',
	'city',
	'state',
	'postal_code',
];

const ADDRESS_OPTIONAL_FIELDS = [
	'address2',
	'company_name',
	'phone_number',
];

export const CREDIT_CARD = 'credit-card';
export const PAYPAL = 'paypal';

export const USA_COUNTRY_CODE = 'USA';
export const CAN_COUNTRY_CODE = 'CAN';

export const USA_CANADA_COUNTRY_CODES = [USA_COUNTRY_CODE, CAN_COUNTRY_CODE];

export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function checkPostalCodeRegex(address) {
	let postalCodeRegex;

	switch (address.country) {
		case 'USA':
			postalCodeRegex = /^\d{5}(?:[-]?\d{4})?$/;
			break;
		case 'CAN':
			postalCodeRegex =
				/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
			break;
		default:
			postalCodeRegex = /^.*$/;
	}

	return new RegExp(postalCodeRegex).test(address.postal_code);
}

export async function validatePostalCode(address) {
	const passesZipRegex = checkPostalCodeRegex(address);

	if (
		passesZipRegex &&
		(address.country === 'USA' || address.country === 'CAN')
	) {
		const request = {
			state: address.state,
			city: address.city,
			country: address.country,
			postal_code: address.postal_code,
		};

		try {
			return (await api.validateAddress(request)).data.is_valid;
		} catch (error) {
			return true;
		}
	}

	return passesZipRegex;
}

export function validateEmail(email) {
	return email.length > 0 && emailRegex.test(email);
}

export function validateAddress(address) {
	const errors = ADDRESS_REQUIRED_FIELDS.filter(
		(prop) => !address[prop] || address[prop].length === 0
	);

	if (!errors.includes('postal_code') && !checkPostalCodeRegex(address)) {
		errors.push('postal_code');
	}

	return errors;
}

export async function getCartData(params, query) {
	const cartParams = {};
	const { offers } = params;

	if (offers) {
		cartParams.offers = offers.split(',');
	}

	const { cpn } = query;

	if (cpn) {
		cartParams.discount = cpn;
	}

	try {
		return (await api.getCart(cartParams)).data;
	} catch (error) {
		ErrorHandler.reportError('Failed to retrieve cart!', error);
		return null;
	}
}

export async function updateCartShippingAddress(address) {
	try {
		return (
			await api.updateCart({
				shipping_address: address,
			})
		).data;
	} catch (error) {
		ErrorHandler.reportError(
			'Failed to update cart shipping address!',
			error
		);
		throw error;
	}
}

export async function updateCartBillingAddress(address) {
	try {
		return (
			await api.updateCart({
				billing_address: address,
			})
		).data;
	} catch (error) {
		ErrorHandler.reportError(
			'Failed to update cart billing address!',
			error
		);
		throw error;
	}
}

export async function syncCartAddressesForPayment(payload) {
	const requestData = {};

	if (payload.billing_address) {
		requestData.billing_address = payload.billing_address;
	}

	if (payload.shipping_address) {
		requestData.shipping_address = payload.shipping_address;
	}

	if (Object.keys(requestData).length === 0) {
		return null;
	}

	return (await api.updateCart(requestData)).data;
}

export async function updateCartShippingMethod(shippingMethodId) {
	try {
		return (await api.setShippingCategory(shippingMethodId)).data;
	} catch (error) {
		ErrorHandler.reportError(
			'Failed to update cart shipping category!',
			error
		);
		return null;
	}
}

export async function getUserData() {
	try {
		return (await api.getUser()).data;
	} catch (error) {
		return null;
	}
}

function hydrateAddress(address) {
	return {
		...DEFAULT_ADDRESS,
		...address,
	};
}

export function addressesHaveSameTaxLocation(addressOne, addressTwo) {
	return (
		addressOne.postal_code ===
		addressTwo.postal_code &&
		addressOne.country ===
		addressTwo.country &&
		addressOne.state ===
		addressTwo.state
	);
}

export async function getUserAddresses() {
	try {
		const addresses = (await api.getAddresses()).data;

		const billingAddress =
			addresses.find((address) => address.is_primary_billing) ||
			addresses.find(
				(address) => ['billing', 'both'].indexOf(address.type) !== -1
			);

		const shippingAddress =
			addresses.find((address) => address.is_primary_shipping) ||
			addresses.find(
				(address) => ['shipping', 'both'].indexOf(address.type) !== -1
			);

		return {
			billingAddress: hydrateAddress(billingAddress),
			shippingAddress: hydrateAddress(shippingAddress),
		};
	} catch (error) {
		return {
			billingAddress: DEFAULT_ADDRESS,
			shippingAddress: DEFAULT_ADDRESS,
		};
	}
}

export function determineGateway() {
	const gatewayMeta = window.atob(
		document.head.querySelector('meta[name="gw"]').content
	);

	let billingComponent = 'Braintree';
	let billingGateway = 'Braintree';

	switch (gatewayMeta) {
		case 'Braintree':
			break;

		case 'Braintree_Testing':
			billingGateway = 'Braintree_Testing';
			break;

		case 'AuthorizeNet_CIM':
			billingGateway = 'AuthorizeNet_CIM';
			billingComponent = 'AuthorizeNet_CIM';
			break;

		case 'Adyen':
			billingGateway = 'Adyen';
			billingComponent = 'Adyen';
			break;

		case 'Adyen_Testing':
			billingGateway = 'Adyen_Testing';
			billingComponent = 'Adyen';
			break;

		default:
			throw new Error(`${gatewayMeta} is not a valid billing gateway!`);
	}

	return {
		billingComponent,
		billingGateway,
	};
}

export const getOfferImage = (media) => {
	if (media.length === 0) return undefined;
	const image = media.find((m) => m.display_order === 1);
	return image ? image.url : media[0].url;
};

export function mapCartItems(offers) {
	return offers
		.filter((offer) => offer.offer && offer.type === 'product')
		.map((offer) => ({
			id: offer.id,
			offerId: offer.offer.id,
			name: offer.offer.title,
			price: offer.amount_base.formatted,
			quantity: offer.quantity,
			image: getOfferImage(offer.offer.media),
		}));
}

export function addOfferToCart(id) {
	return api.addOfferToCart({
		id,
		quantity: 1
	});
}

export function removeOfferFromCart(id) {
	return api.removeOfferFromCart(id);
}

export function mapCartCoupons(offers) {
	return offers
		.filter((offer) => offer.offer_snapshot && offer.type === 'discount')
		.map((offer) => ({
			id: offer.id,
			name: offer.offer_snapshot.title,
			price: offer.amount_local.formatted,
		}));
}

export function mapShippingOptions(categories) {
	return categories.map((category) => ({
		id: category.id,
		name: category.name,
		detail: category.detail,
		price: _.get(category, 'price_local.amount.formatted'),
		price_decimal: _.get(category, 'price_local.amount.decimal'),
	}));
}

export function getPriceBreakdown(cart, showTaxShipping) {
	const breakdown = {
		currency: _.get(cart, 'amount_base.currency'),
		subTotal: _.get(cart, 'subtotal_base.formatted'),
		tax: null,
		duties: null,
		fees: null,
		shipping: null,
		total: _.get(cart, 'subtotal_base.formatted'),
	};

	if (showTaxShipping) {
		breakdown.shipping = _.get(
			cart,
			'shipping_category.price_local.amount.formatted'
		);
		breakdown.tax = _.get(cart, 'tax_amount_local.formatted');
		breakdown.duties = _.get(cart, 'duties_amount_local.formatted');
		breakdown.fees = _.get(cart, 'fees', []);
		breakdown.total = _.get(cart, 'amount_local.formatted');
	}

	return breakdown;
}

export async function checkEmailExists(email) {
	try {
		const { data } = await api.checkEmail(email);
		return data.exists;
	} catch (error) {
		ErrorHandler.reportError('Failed to check email address!', error);
		return false;
	}
}

export const PAYPAL_COUNTRY_CODES = {
	US: 'USA',
	CA: 'CAN',
};

export function mapShippingOptionsToPaypal(
	shippingOptions,
	cart,
	paypalSelectedShippingOption
) {
	let paypalShippingOptions = shippingOptions.map((option) => {
		const selected =
			paypalSelectedShippingOption &&
			paypalSelectedShippingOption.id === option.id.toString();

		const amount = {
			value: option.price_decimal,
			currency: 'USD',
		};

		return {
			id: option.id.toString(),
			type: 'SHIPPING',
			label: option.name,
			selected,
			amount,
		};
	});

	let selectedOption = paypalShippingOptions.find(
		(option) => option.selected
	);

	if (!selectedOption) {
		paypalShippingOptions = paypalShippingOptions.map((option) => ({
			...option,
			selected: option.id === cart.shipping_category.id.toString(),
		}));

		selectedOption = paypalShippingOptions.find(
			(option) => option.selected
		);
	}

	return {
		shippingOptions: paypalShippingOptions,
		selectedOption
	};
}
